import type {
  DocNetIssue,
  DocNetIssueCreate,
  DocNetIssueStatus,
  DocNetComment,
  DocNetSubIssue,
  DocNetAgentRun,
} from 'src/@types/docnet';
import DocNetBaseService from 'src/services/docnet/DocNetBaseService';
import { api } from 'src/pages/APIs';

export default class DocNetIssueService extends DocNetBaseService {
  public async getIssues(filters?: {
    status?: string;
    priority?: string;
    assignee_id?: string;
    mission_id?: string;
    q?: string;
    skip?: number;
    limit?: number;
  }): Promise<{ items: DocNetIssue[]; total: number }> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.priority) params.set('priority', filters.priority);
    if (filters?.assignee_id) params.set('assignee_id', filters.assignee_id);
    if (filters?.mission_id) params.set('mission_id', filters.mission_id);
    if (filters?.q) params.set('q', filters.q);
    if (filters?.skip !== undefined) params.set('skip', String(filters.skip));
    if (filters?.limit !== undefined) params.set('limit', String(filters.limit));
    const qs = params.toString();
    const url = api.docnet_issues + (qs ? `?${qs}` : '');
    const response = await super.get(url, { headers });
    if (Array.isArray(response)) {
      return { items: response as DocNetIssue[], total: response.length };
    }
    const items = (response as any).items || [];
    const total = (response as any).total ?? items.length;
    return { items, total };
  }

  public async getIssueById(id: string): Promise<DocNetIssue | undefined> {
    const headers = await this.getHeaders();
    try {
      return (await super.get(`${api.docnet_issues}/${id}`, {
        headers,
      })) as DocNetIssue;
    } catch {
      return undefined;
    }
  }

  public async createIssue(data: DocNetIssueCreate): Promise<DocNetIssue> {
    const headers = await this.getHeaders();
    return (await super.post(api.docnet_issues, data, {
      headers,
    })) as DocNetIssue;
  }

  public async updateIssue(
    id: string,
    data: Partial<DocNetIssue>,
  ): Promise<DocNetIssue> {
    const headers = await this.getHeaders();
    return (await super.patch(`${api.docnet_issues}/${id}`, data, {
      headers,
    })) as DocNetIssue;
  }

  public async deleteIssue(id: string): Promise<void> {
    const headers = await this.getHeaders();
    await super.delete(`${api.docnet_issues}/${id}`, { headers });
  }

  public async addComment(
    taskId: string,
    content: string,
    authorName: string,
  ): Promise<DocNetComment> {
    const headers = await this.getHeaders();
    return (await super.post(
      `${api.docnet_issues}/${taskId}/comments`,
      { content, author_name: authorName },
      { headers },
    )) as DocNetComment;
  }

  /**
   * Fetch a single comment's full body by id.
   *
   * The mission timeline persists a truncated preview on each
   * activity row (~1.5 KB) so per-mission timeline reads stay cheap.
   * When the user expands a comment row to see the full text, the
   * panel calls this endpoint to lazy-load the complete body — one
   * primary-key fetch, no over-fetching of sibling comments.
   *
   * Returns ``undefined`` for not-found / deleted comments so the
   * caller can fall back to the persisted preview without a hard
   * error.
   */
  public async getCommentById(
    commentId: string,
  ): Promise<DocNetComment | undefined> {
    const headers = await this.getHeaders();
    try {
      return (await super.get(
        `${api.docnet_issue_comments}/${commentId}`,
        { headers },
      )) as DocNetComment;
    } catch {
      return undefined;
    }
  }

  public async updateSubIssue(
    taskId: string,
    subTaskId: string,
    status: DocNetIssueStatus,
  ): Promise<DocNetSubIssue> {
    const headers = await this.getHeaders();
    return (await super.patch(
      `${api.docnet_issues}/${taskId}/subtasks/${subTaskId}`,
      { status },
      { headers },
    )) as DocNetSubIssue;
  }

  public async getRuns(issueId: string): Promise<DocNetAgentRun[]> {
    const headers = await this.getHeaders();
    const url = `${api.docnet_issues}/${issueId}/runs`;
    const response = await super.get(url, { headers });
    return ((response as { items?: DocNetAgentRun[] }).items || []) as DocNetAgentRun[];
  }

  public async triggerRun(issueId: string): Promise<{ run_id: string; status: string }> {
    const headers = await this.getHeaders();
    const url = `${api.docnet_issues}/${issueId}/run`;
    return (await super.post(url, {}, { headers })) as { run_id: string; status: string };
  }

  public async getRun(issueId: string, runId: string): Promise<DocNetAgentRun> {
    const headers = await this.getHeaders();
    const url = `${api.docnet_issues}/${issueId}/runs/${runId}`;
    return (await super.get(url, { headers })) as DocNetAgentRun;
  }

  public async stopRun(issueId: string, runId: string): Promise<void> {
    const headers = await this.getHeaders();
    const url = `${api.docnet_issues}/${issueId}/runs/${runId}/stop`;
    await super.post(url, {}, { headers });
  }
}
